import { Divider } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';

import { FEATURE_FLAGS } from '../../config/featureFlags';
import MapyCzContext from '../../context/MapyCzContext';
import { ResultMap } from '../LeafletMap';
import useSMapResize from '../../hooks/useSMapResize';
import { mapLayers } from '../../redux/reducers/game';
import {
    drawAllResultsLayerToMap,
    getMapInstanceByGameMode,
    setupMapInstance,
    setupMarkerLayerOnly,
} from '../../util/map';

/**
 * OPTIMALIZACE: ResultSMap s optimalizovanou marker vrstvou
 * - Použita marker vrstva bez POI (šetří requesty)
 * - Automatické přizpůsobení pohledu na všechny body
 */
const ResultSMap = ({ guessedPoints, mode, city, radius }) => {
    const { width } = useSMapResize();
    const resultMap = useRef();
    const mapyContext = useContext(MapyCzContext);

    const [layerWithMarksRef, setLayerWithMarksRef] = useState();
    const [vectorLayerRef, setVectorLayerRef] = useState();
    const [mapInstanceRef, setMapInstanceRef] = useState();
    const [mapLoaded, setMapLoaded] = useState(false);

    /**
     * Inicializace mapy (pouze pro staré API)
     */
    useEffect(() => {
        // Přeskočit pokud používáme nové API
        if (FEATURE_FLAGS.USE_NEW_MAP_API) return;

        if (mapyContext.loadedMapApi && !mapLoaded && resultMap.current) {
            const { SMap: SMapContext } = mapyContext;
            const mapInstance = getMapInstanceByGameMode(SMapContext, mode, city, radius, resultMap.current);

            // Setup s optimalizovaným lazy loading
            setupMapInstance(SMapContext, mapInstance, mapLayers.default, false);

            // OPTIMALIZACE: Použijeme marker vrstvu bez POI (šetří requesty)
            const layerWithMarks = setupMarkerLayerOnly(SMapContext, mapInstance);

            const vectorLayer = new SMapContext.Layer.Geometry();
            mapInstance.addLayer(vectorLayer);
            vectorLayer.enable();

            setVectorLayerRef(vectorLayer);
            setLayerWithMarksRef(layerWithMarks);
            setMapInstanceRef(mapInstance);
            setMapLoaded(true);
        }
    }, [mapyContext.loadedMapApi, mapLoaded, mode, city, radius]);

    /**
     * Překreslení výsledků při změně guessedPoints (pouze pro staré API)
     */
    useEffect(() => {
        // Přeskočit pokud používáme nové API
        if (FEATURE_FLAGS.USE_NEW_MAP_API) return;

        if (mapyContext.loadedMapApi && mapInstanceRef && layerWithMarksRef && vectorLayerRef) {
            // Vyčistíme předchozí výsledky
            layerWithMarksRef.removeAll();
            vectorLayerRef.removeAll();

            // Vykreslíme nové výsledky
            drawAllResultsLayerToMap(
                mapyContext.SMap,
                mapInstanceRef,
                layerWithMarksRef,
                vectorLayerRef,
                guessedPoints,
            );

            // Přizpůsobíme pohled na všechny body
            if (guessedPoints && guessedPoints.length > 0) {
                const allCoords = [];
                guessedPoints.forEach(point => {
                    if (point) {
                        allCoords.push(point.pointPanorama);
                        allCoords.push(point.pointMap);
                    }
                });

                if (allCoords.length > 0) {
                    // Vypočítáme bounding box a nastavíme pohled
                    const coordsArray = allCoords.map(coord =>
                        mapyContext.SMap.Coords.fromWGS84(coord.x, coord.y)
                    );

                    // Použijeme computeCenterZoom pro optimální pohled
                    const [center, zoom] = mapInstanceRef.computeCenterZoom(coordsArray);
                    mapInstanceRef.setCenterZoom(center, Math.min(zoom, 15), true);
                }
            }
        }
    }, [mapyContext.loadedMapApi, mapyContext.SMap, guessedPoints, mapInstanceRef, layerWithMarksRef, vectorLayerRef]);

    // Pokud je zapnutý nový map API, použijeme Leaflet
    if (FEATURE_FLAGS.USE_NEW_MAP_API) {
        return <ResultMap guessedPoints={guessedPoints} />;
    }

    return (
        <div id="smap-container">
            {width <= 961 ? <Divider /> : null}
            <div id="smap" className="smap-result smap-style" ref={resultMap} />
        </div>
    );
};

export default ResultSMap;
